import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Activity, CheckCircle2, CircleSlash, FlaskConical, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'

import { healthQuery, type AiStatus } from '@/api/client'
import { keys } from '@/api/keys'
import { testAi } from '@/api/settings'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { formatClock, formatMs } from '@/lib/format'

/**
 * Is the configured AI reachable, how is it billed, and does a real call work?
 * Provider routing can change between calls, so do not assume a Claude model
 * or a fixed test price here.
 */

const STATUS_TONE: Record<AiStatus, BadgeTone> = { ok: 'ok', degraded: 'warn', down: 'danger', mock: 'info' }
const STATUS_LABEL: Record<AiStatus, string> = { ok: 'Working', degraded: 'Patchy', down: 'Not reachable', mock: 'Mock mode' }
const STATUS_ICON: Record<AiStatus, ReactNode> = {
  ok: <CheckCircle2 aria-hidden />,
  degraded: <TriangleAlert aria-hidden />,
  down: <CircleSlash aria-hidden />,
  mock: <FlaskConical aria-hidden />,
}

const BILLING_TEXT: Record<string, string> = {
  api: 'Billed per call against an API key. Costs below are list-price estimates.',
  subscription: 'Uses your provider account. Reported costs are estimates for comparison, not a bill; some providers do not report costs.',
  mock: 'Nothing is sent anywhere. Annotations and stories are generated locally.',
}

export function AiHealthCard() {
  const qc = useQueryClient()
  const health = useQuery(healthQuery)
  const ai = health.data?.ai

  const ping = useMutation({
    mutationFn: testAi,
    onSuccess: (r) => {
      toast.success(`AI answered in ${formatMs(r.duration_ms)}`, { description: r.model })
    },
    onError: (e) => toast.error('The test call did not go through', { description: e instanceof Error ? e.message : String(e) }),
    onSettled: () => { void qc.invalidateQueries({ queryKey: keys.health() }) },
  })

  return (
    <section className="card p-5" aria-labelledby="ai-health-heading">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="ai-health-heading" className="flex items-center gap-2 text-lg font-semibold text-ink">
          <Activity className="size-5 text-ink-2" aria-hidden />
          AI connection
        </h2>
        {ai ? (
          <Badge tone={STATUS_TONE[ai.status]} className="gap-1 [&_svg]:size-3.5">
            {STATUS_ICON[ai.status]}
            {STATUS_LABEL[ai.status]}
          </Badge>
        ) : null}
      </header>

      {health.isPending ? (
        <p className="mt-3 text-sm text-ink-2">Checking…</p>
      ) : !ai ? (
        <p className="mt-3 text-sm text-ink-2">The studio server is not answering, so the AI connection cannot be checked right now.</p>
      ) : (
        <>
          <p className="mt-3 text-sm text-ink-2">{ai.mode === 'openrouter'
            ? 'Billed through your OpenRouter API key. Costs use OpenRouter-reported charges; missing cost data stays unavailable.'
            : BILLING_TEXT[ai.billing ?? ''] ?? 'Billing method unknown.'}</p>

          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <Row label="CLI version" value={ai.cli_version ?? '—'} />
            <Row label="Signed in with" value={ai.auth_method ?? '—'} />
            <Row label="Last checked" value={ai.checked_at ? formatClock(ai.checked_at) : '—'} />
            {ai.clock_skew_s != null && Math.abs(ai.clock_skew_s) > 60 ? <Row label="Clock difference" value={`${Math.round(ai.clock_skew_s)} s`} warn /> : null}
          </dl>

          {ai.last_error ? (
            <p className="mt-3 rounded-xl border border-danger bg-danger-soft px-3 py-2 text-sm text-ink" role="status">
              {ai.last_error}
            </p>
          ) : null}

          {health.data?.jobs?.paused_until ? (
            <p className="mt-3 rounded-xl border border-warn bg-warn-soft px-3 py-2 text-sm text-ink" role="status">
              AI work is paused until {formatClock(health.data.jobs.paused_until)}. Queued jobs carry on by themselves afterwards.
            </p>
          ) : null}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button onClick={() => ping.mutate()} loading={ping.isPending}>
              Send a test message
            </Button>
            <Button variant="ghost" onClick={() => void health.refetch()} loading={health.isFetching}>
              Check again
            </Button>
          </div>
          <p className="mt-2 text-xs text-ink-3">Testing sends one real request and may consume your provider account quota.</p>
        </>
      )}
    </section>
  )
}

function Row({ label, value, warn = false }: { label: string; value: string; warn?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-ink-3">{label}</dt>
      <dd className={warn ? 'font-medium text-warn' : 'font-medium text-ink'}>{value}</dd>
    </div>
  )
}
